import { Rgb, Scene, WallpaperStyleInfo, darken, lighten, luminance, mix, prng, rgba, share, valueNoise } from '../wallpaper-core';

export const INFO: WallpaperStyleInfo = {
  key: 'hexagon', label: 'Hexagon',
  blurb: 'A honeycomb of beveled hexagons with a few lit cells, the tech grid look',
  params: [ share( 'size', 'Size', 45 ), share( 'gap', 'Gap', 30 ) ],
};

/**
 * Hexagon: a pointy top honeycomb of bevelled cells with grout between them and a few lit cells.
 * Each cell's tile comes from a low frequency noise read at its centre, so the colours form islands
 * that drift across the picture instead of confetti. Vectors only, about 1500 paths at most.
 */
export function draw( { ctx, w, h, box, colors, rand, p, seed }: Scene ): void {
  const white: Rgb = { r: 255, g: 255, b: 255 };
  const s = box.w / 1920;
  // the tiles' order: the first tile is the grout and the background, the cells walk the rest in order
  const cellTiles = colors.length > 1 ? colors.slice( 1 ) : colors;
  // a two color palette leaves one cell colour, so the islands would not show at all: the walk
  // becomes three tones of that one tile and the noise still reads as a drift across the picture
  const tiles = cellTiles.length > 1 ? cellTiles
    : [ lighten( cellTiles[ 0 ], 0.14 ), cellTiles[ 0 ], darken( cellTiles[ 0 ], 0.16 ) ];
  let grout = colors[ 0 ];
  // grout and cells of one luminance flatten to a sheet, so a flat palette gets its grout pushed away
  // from the cells: darker when there is room below it, lighter when it is already near black
  const contrast = tiles.reduce( ( sum, c ) => sum + Math.abs( luminance( c ) - luminance( grout ) ), 0 ) / tiles.length;
  if ( contrast < 0.2 ) {
    const k = 1 - contrast / 0.2;
    grout = luminance( grout ) > 0.16 ? darken( grout, 0.25 + k * 0.35 ) : lighten( grout, 0.1 + k * 0.2 );
  }
  ctx.fillStyle = rgba( grout, 1 );
  ctx.fillRect( 0, 0, w, h );

  // Size: 40 columns across the box at 0, 8 at 100; pointy top, so a column is √3 R wide and a row 1.5 R tall
  const cols = Math.round( 40 - p[ 0 ] * 32 );
  const cw = box.w / cols;
  const R = cw / Math.sqrt( 3 );
  const rh = R * 1.5;
  // Gap: the grout channel, from touching cells to channels near half a cell wide
  const gap = cw * p[ 1 ] * 0.42;
  const hair = Math.max( 1, s );

  // the colour islands: one low frequency noise over the box (4 x 3 lattice), read once per cell
  const noise = valueNoise( rand, 4 );
  interface Cell { x: number; y: number; n: number; lit: boolean; tile: number; }
  const cells: Cell[] = [];
  // rows and columns counted from the box's top left, extended past the canvas so no edge shows a half lattice
  const c0 = Math.floor( ( 0 - box.x ) / cw ) - 1, c1 = Math.ceil( ( w - box.x ) / cw ) + 1;
  const r0 = Math.floor( ( 0 - box.y ) / rh ) - 1, r1 = Math.ceil( ( h - box.y ) / rh ) + 1;
  for ( let r = r0; r <= r1; r++ ) {
    for ( let c = c0; c <= c1; c++ ) {
      // every cell's jitter and lit roll come from its own (seed, row, column) generator, so the
      // lattice is the same whatever the canvas
      const g = prng( ( seed * 31 + r * 1000003 + c * 7919 ) >>> 0 );
      const x = box.x + ( c + ( r & 1 ? 0.5 : 0 ) ) * cw;
      const y = box.y + r * rh;
      // a little jitter frays the island edges, so the borders read as honeycomb and not as contour lines
      const n = noise( ( x - box.x ) / box.w, ( y - box.y ) / box.h * 0.75 ) + ( g() - 0.5 ) * 0.06;
      cells.push( { x, y, n, lit: g() < 0.06, tile: 0 } );
    }
  }
  // the tiles are dealt by rank, not by raw value: every colour gets its share of cells (value noise
  // crowds the middle) and the order still follows the noise, tile 2 in the low regions, the last on top
  const order = cells.map( ( _, i ) => i ).sort( ( a, b ) => cells[ a ].n - cells[ b ].n );
  order.forEach( ( idx, rank ) => cells[ idx ].tile = Math.min( tiles.length - 1, Math.floor( rank / order.length * tiles.length ) ) );

  // every cell is drawn at the origin under a translate, so one bevel gradient per tile serves the whole lattice
  const hex = ( r: number ) => {
    ctx.beginPath();
    for ( let k = 0; k < 6; k++ ) {
      const a = Math.PI / 6 + k * Math.PI / 3;
      const px = r * Math.cos( a ), py = r * Math.sin( a );
      k ? ctx.lineTo( px, py ) : ctx.moveTo( px, py );
    }
    ctx.closePath();
  };
  // the bevel: lit from the top left corner to the bottom right, the same on every cell so the sheet reads as one relief
  const d = R * 0.72;
  const bevels = tiles.map( c => {
    const g = ctx.createLinearGradient( -d, -d, d, d );
    g.addColorStop( 0, rgba( lighten( c, 0.18 ), 1 ) );
    g.addColorStop( 1, rgba( darken( c, 0.22 ), 1 ) );
    return g;
  } );
  for ( const cell of cells ) {
    ctx.setTransform( 1, 0, 0, 1, cell.x, cell.y );
    hex( R );
    ctx.fillStyle = bevels[ cell.tile ];
    ctx.fill();
    // the grout, or at Gap 0 a hairline of the cell's own bevel that closes the antialiasing seam between touching cells
    if ( gap >= hair ) {
      ctx.strokeStyle = rgba( grout, 1 );
      ctx.lineWidth = gap;
    } else {
      ctx.strokeStyle = bevels[ cell.tile ];
      ctx.lineWidth = hair;
    }
    ctx.stroke();
  }

  // the lit cells last, so the rim sits on the grout's inner edge: a small glow of the cell's colour
  // toward white, fading out well inside the cell so it reads as a lit tile and never as glass
  const rIn = R - ( gap + hair ) / Math.sqrt( 3 );
  const reach = rIn * Math.sqrt( 3 ) / 2 * 0.95;
  for ( const cell of cells ) {
    if ( !cell.lit ) continue;
    const c = tiles[ cell.tile ];
    const lit = mix( c, white, 0.5 );
    const glow = ctx.createRadialGradient( 0, 0, 0, 0, 0, reach );
    glow.addColorStop( 0, rgba( lit, 0.6 ) );
    glow.addColorStop( 0.5, rgba( lit, 0.22 ) );
    glow.addColorStop( 1, rgba( lit, 0 ) );
    ctx.setTransform( 1, 0, 0, 1, cell.x, cell.y );
    hex( rIn );
    ctx.fillStyle = glow;
    ctx.fill();
    ctx.strokeStyle = rgba( lighten( c, 0.5 ), 0.85 );
    ctx.lineWidth = hair;
    ctx.stroke();
  }
  ctx.setTransform( 1, 0, 0, 1, 0, 0 );
}
